import React, { useEffect, useRef, useState } from "react"

import SocialProofComponent from "../../components/social_proof/SocialProofComponent"
import useDeviceDetection from "../../hooks/useDeviceDetection"
import useWindowSize from "../../hooks/useWindowSize"
import { detectLocationWithFallbacks } from "../../utils/geolocation"
import { distanceInMiles } from "../../utils/utils"

import HomePageV2Desktop from "./HomePageV2Desktop"
import HomePageV2Mobile from "./HomePageV2Mobile"

function getPractitionerDistance(practitioner, userCoordinates) {
  if (practitioner?.practice?.distanceMiles != null) return practitioner.practice.distanceMiles
  if (userCoordinates && Array.isArray(userCoordinates) && userCoordinates.length === 2) {
    const [userLat, userLng] = userCoordinates
    const practitionerLat = practitioner?.practice?.latitude
    const practitionerLng = practitioner?.practice?.longitude
    if (practitionerLat != null && practitionerLng != null) {
      return distanceInMiles(userLat, userLng, practitionerLat, practitionerLng)
    }
  }
  return Infinity
}

function sortPractitionersByCloseness(practitioners, userCoordinates) {
  if (!Array.isArray(practitioners) || practitioners.length === 0) return practitioners
  return [...practitioners].sort((a, b) => {
    const distanceA = getPractitionerDistance(a, userCoordinates)
    const distanceB = getPractitionerDistance(b, userCoordinates)
    if (distanceA === distanceB && distanceA === Infinity) return 0
    return distanceA - distanceB
  })
}

const HomePageV2 = ({
  reviews,
  therapyCategories,
  isMobile: serverIsMobile,
  virtualPractitioners,
  initialLocation
}) => {
  const getInitialLocation = () => (initialLocation?.cityState ? initialLocation.cityState : "")
  const initialLoc = getInitialLocation()
  const [currentLocation, setCurrentLocation] = useState(initialLoc)
  const [featuredPractitioners, setFeaturedPractitioners] = useState([])
  const hasValidInitialLocation = typeof window !== "undefined" && initialLoc && initialLoc !== "97703"
  const [isLoadingPractitioners, setIsLoadingPractitioners] = useState(hasValidInitialLocation)
  const previousLocationRef = useRef(null)

  useEffect(() => {
    if (typeof window === "undefined" || typeof document === "undefined") return
    async function initPlaces() {
      if (window.google?.maps) {
        await window.google.maps.importLibrary("places")
        document.dispatchEvent(new CustomEvent("googlePlacesLoaded"))
      }
    }
    initPlaces()
  }, [])

  useEffect(() => {
    if (typeof window === "undefined") return
    if (initialLocation?.cityState && initialLocation.cityState !== "97703") return
    let isCancelled = false
    const initializeLocation = async () => {
      const location = await detectLocationWithFallbacks(initialLocation)
      if (!isCancelled && location.cityState && location.cityState !== "97703") {
        setCurrentLocation(location.cityState)
        sessionStorage.setItem("cityState", location.cityState)
        if (location.latlng && Array.isArray(location.latlng) && location.latlng.length === 2) {
          sessionStorage.setItem("latlng", JSON.stringify(location.latlng))
        }
        document.dispatchEvent(new CustomEvent("homepageLocationUpdated", { detail: location.cityState }))
      }
    }
    initializeLocation()
    return () => {
      isCancelled = true
    }
  }, [initialLocation])

  useEffect(() => {
    if (typeof document === "undefined") return
    const handleLocationUpdate = (e) => setCurrentLocation(e.detail)
    document.addEventListener("homepageLocationUpdated", handleLocationUpdate)
    return () => document.removeEventListener("homepageLocationUpdated", handleLocationUpdate)
  }, [])

  useEffect(() => {
    if (typeof window === "undefined") return
    if (!currentLocation || currentLocation === "97703") return
    if (currentLocation === previousLocationRef.current) return
    previousLocationRef.current = currentLocation

    const fetchPractitionersByLocation = async (location) => {
      setIsLoadingPractitioners(true)
      try {
        // Prefer server-provided coordinates when they match current location to avoid
        // showing non-local practitioners due to stale sessionStorage (e.g. from another region/VPN).
        const serverLatLng =
          initialLocation?.cityState === location &&
          initialLocation?.latlng &&
          Array.isArray(initialLocation.latlng) &&
          initialLocation.latlng.length === 2
            ? initialLocation.latlng
            : null

        const storedLatLng = sessionStorage.getItem("latlng")
        let locationToFetch = location
        let parsedCoordinates = null

        if (serverLatLng) {
          locationToFetch = `${serverLatLng[0]},${serverLatLng[1]}`
          parsedCoordinates = serverLatLng
        } else if (storedLatLng) {
          try {
            const parsed = JSON.parse(storedLatLng)
            if (Array.isArray(parsed) && parsed.length === 2) {
              locationToFetch = `${parsed[0]},${parsed[1]}`
              parsedCoordinates = parsed
            }
          } catch (e) {
            // use location as is
          }
        }

        const response = await fetch(`/public/featured_practitioners?location=${encodeURIComponent(locationToFetch)}`)
        if (response.ok) {
          const data = await response.json()
          const filtered = (data.practitioners || []).filter((p) => (p?.practice?.reviewsCount || 0) >= 3)
          const sorted = sortPractitionersByCloseness(filtered, parsedCoordinates)
          setFeaturedPractitioners(sorted)
        } else {
          setFeaturedPractitioners([])
        }
      } catch (error) {
        setFeaturedPractitioners([])
      } finally {
        setIsLoadingPractitioners(false)
      }
    }
    fetchPractitionersByLocation(currentLocation)
  }, [currentLocation, initialLocation])

  const { isMobile: isMobileDeviceDetection, width: deviceDetectionWidth } = useDeviceDetection(1080)
  const { isMobile: isMobileWindowSize, width: windowSizeWidth } = useWindowSize(1080)
  const [hasMounted, setHasMounted] = useState(false)
  useEffect(() => setHasMounted(true), [])

  const determineIsMobile = () => {
    if (!hasMounted) return serverIsMobile ?? false
    const width = windowSizeWidth !== null && windowSizeWidth !== undefined ? windowSizeWidth : deviceDetectionWidth
    if (width === null || width === undefined) return serverIsMobile ?? false
    if (isMobileDeviceDetection === isMobileWindowSize) return isMobileDeviceDetection
    if (isMobileWindowSize) return true
    if (isMobileDeviceDetection) return true
    return false
  }

  const isMobile = determineIsMobile()
  const filterPractitioners = (practitioners) => {
    if (!Array.isArray(practitioners)) return []
    return practitioners.filter((p) => (p?.practice?.reviewsCount || 0) >= 3)
  }

  const filteredFeatured = filterPractitioners(featuredPractitioners)
  const filteredVirtual = filterPractitioners(virtualPractitioners || [])

  const commonProps = {
    reviews,
    therapyCategories,
    featuredPractitioners: filteredFeatured,
    virtualPractitioners: filteredVirtual,
    currentLocation,
    isLoadingPractitioners,
    initialLocation
  }

  return (
    <>
      <SocialProofComponent isMobile={isMobile} />
      {isMobile ? <HomePageV2Mobile {...commonProps} /> : <HomePageV2Desktop {...commonProps} />}
    </>
  )
}

export default HomePageV2
